import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from '../../../constants/apiConfig';
import { useActualIncidentId } from './commonHooks';
import { toast } from 'react-toastify';

export type ComsListItem = {
  id: string;
  comsListItemId: string;
  incidentId: string;
  workGroupId?: string | null;
  name?: string | null;
  cellPhoneNumber?: string | null;
  rxFrequency?: string | null;
  other?: string | null;
  archived: boolean;
  version: number;
  timestamp: string;
  userGuid?: string | null;
};

export type CreateComsListItemData = {
  workGroupId?: string | null;
  name?: string | null;
  cellPhoneNumber?: string | null;
  rxFrequency?: string | null;
  other?: string | null;
};

export type UpdateComsListItemData = {
  workGroupId?: string | null;
  name?: string | null;
  cellPhoneNumber?: string | null;
  rxFrequency?: string | null;
  other?: string | null;
  archived?: boolean;
};

export const comsListKeys = {
  base: ['comsList'] as const,
  byIncident: (incidentId: string) =>
    [...comsListKeys.base, incidentId] as const,
} as const;

export const useIncidentComsList = () => {
  const incidentId = useActualIncidentId();
  return useQuery({
    queryKey: comsListKeys.byIncident(incidentId),
    queryFn: async () => {
      const resp = await axios.get<ComsListItem[]>(
        `${config.apiGateway.ics_incident_coms_list}/${incidentId}`
      );
      return resp.data;
    },
    enabled: !!incidentId,
  });
};

export const useCreateComsListItem = () => {
  const incidentId = useActualIncidentId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateComsListItemData) => {
      const resp = await axios.post<ComsListItem>(
        config.apiGateway.ics_coms_list,
        {
          ...data,
          incidentId,
        }
      );
      return resp.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: comsListKeys.byIncident(incidentId),
      });
    },
    onError: (error: unknown) => {
      const err = error as { message?: string };
      toast.error(err.message ?? 'Failed to add contact');
    },
  });
};

export const useUpdateComsListItem = () => {
  const incidentId = useActualIncidentId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: UpdateComsListItemData;
    }) => {
      const resp = await axios.put<ComsListItem>(
        `${config.apiGateway.ics_coms_list}/${incidentId}/${id}`,
        data
      );
      return resp.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: comsListKeys.byIncident(incidentId),
      });
    },
    onError: (error: unknown) => {
      const err = error as { message?: string };
      toast.error(err.message ?? 'Failed to update contact');
    },
  });
};

export const useDeleteComsListItem = () => {
  const incidentId = useActualIncidentId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const resp = await axios.delete<ComsListItem>(
        `${config.apiGateway.ics_coms_list}/${incidentId}/${id}`
      );
      return resp.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: comsListKeys.byIncident(incidentId),
      });
    },
    onError: (error: unknown) => {
      const err = error as { message?: string };
      toast.error(err.message ?? 'Failed to delete contact');
    },
  });
};
